import payment from '@/services/http/api/payment'

import { PAYMENT_STATUS, SOFT_PAYMENT_LIMIT } from '@/constants/payment'
import { PaymentType, PayerType } from '@/types'

import useTransactionDataStore from '@/stores/transactionData'
import useResourceStore from '@/stores/resource'
import { useSummaryDataStore } from '@/stores/summary'

import { storeToRefs } from 'pinia'

// Todo: Refactor to Pinia. Add schema for payload that we send when creating payment
// Schema should be different for company and private person
// Refactor donor-data.ts: types should be defined infering from schema

export default {
  namespaced: true,
  state: {
    orderId: null,
    paymentId: null,
    formData: {},
    STATUS_MAX_REQUEST: 4,
    STATUS_INTERVAL_MS: 3000,
    statusResponse: null,
    counter: 0,
    lastRequest: false
  },
  getters: {
    getOrderId: (state) => state.orderId,
    getPaymentId: (state) => state.paymentId,
    getStatusResponse: (state) => state.statusResponse,
    isLastRequest: (state) => state.counter >= state.STATUS_MAX_REQUEST || state.lastRequest,
    getSummary: (state) => state.summary
  },
  mutations: {
    setOrderId(state, payload) {
      state.orderId = payload
    },
    setPaymentId(state, payload) {
      state.paymentId = payload
    },
    setFormData(state, payload) {
      state.formData = payload
    },
    setStatusResponse(state, payload) {
      state.statusResponse = payload
    },
    incrementCounter(state) {
      state.counter += 1
    },
    setLastRequest(state, payload) {
      state.lastRequest = payload
    },
    setSummary(state, payload) {
      state.summary = payload
    }
  },
  actions: {
    async send({ rootGetters, _, state, commit }) {
      const transactionDataStore = useTransactionDataStore()
      const { transactionData, methodType, paymentType, getSelectedOrOtherAmount } =
        storeToRefs(transactionDataStore)
      const donorData = {
        ...transactionData.value
      }
      const paymentAmount = getSelectedOrOtherAmount.value
      commit('setFormData', {})
      const { formData } = state

      // default data
      formData.childEdition = rootGetters['child/getChild'].id
      formData.type = paymentType.value
      formData.method = methodType.value

      // donor personal data fields
      Object.entries(donorData.donor).forEach(([key, val]) => {
        if (key === 'phoneNumber' && val) {
          formData[key] = val && val.replace(/\s/g, '')
        } else {
          formData[key] = val
        }
      })

      // anonymous field
      // due to different possible location of anonymous field in form
      // Vee-validate could save value to array. Backend is expecting boolean.
      formData.anonymous = Array.isArray(donorData.anonymous)
        ? donorData.anonymous[0] ?? false
        : donorData.anonymous ?? false

      // amount of installments
      if (formData.type === PaymentType.instalments) {
        formData.installments = Math.ceil(SOFT_PAYMENT_LIMIT / paymentAmount)
      }

      // start fundraising or donate to
      if (
        formData.type === PaymentType.fundraising ||
        formData.type === PaymentType.fundraisingDonate
      ) {
        formData.amount = paymentAmount * 100
      }

      // address fields
      if (donorData.payerType === PayerType.company) {
        Object.entries(donorData.address).forEach(([key, val]) => {
          if (key === 'postalCode') {
            formData[key] = val && val.replace('-', '')
          } else {
            formData[key] = val
          }
        })
      }

      // company fields
      if (donorData.payerType === PayerType.company) {
        formData.companyName = donorData.companyName
      }

      // nip field
      if (donorData.payerType === PayerType.company) {
        formData.nipNumber = donorData.nipNumber
      }

      // isCompany field
      if (donorData.payerType === PayerType.company) {
        formData.isCompany = true
      }

      // consents object
      if (donorData.consents) {
        formData.consents = { ...donorData.consents }

        const resourceStore = useResourceStore()
        const { getConsents: consents } = storeToRefs(resourceStore)

        const consentsFields =
          consents.value?.fieldsets?.find((array) => array.name === 'consents')?.fields ?? []
        const consentsText = consentsFields.filter((array) => array.type === 'text')

        // make all consents with type "text" true, because user
        // accepts them automaticaly while submitting the form
        consentsText?.forEach((consent) => {
          if (!consent?.name) return
          formData.consents[consent.name] = true
        })
      }

      const summaryDataStore = useSummaryDataStore()

      return payment.payment(formData).then((response) => {
        const { data } = response
        summaryDataStore.saveSummaryData({ sessionId: data.data.p24_session_id })
        return response
      })
    },
    getStatus(context, payload) {
      return payment
        .paymentStatus(payload)
        .then(({ data }) => {
          context.commit('setStatusResponse', data?.data || null)
          if (data.data?.status?.id === PAYMENT_STATUS.paid) {
            context.commit('setLastRequest', true)
          }
        })
        .catch(() => {
          context.commit('setStatusResponse', context.getters.isLastRequest ? false : null)
        })
        .finally(() => {
          setTimeout(() => {
            context.dispatch('getPaymentStatus', payload)
          }, context.state.STATUS_INTERVAL_MS)
        })
    },
    getPaymentStatus(context, payload) {
      const paid = context.state.statusResponse && context.state.statusResponse.status.id === 4
      if (context.state.counter < context.state.STATUS_MAX_REQUEST && !paid) {
        setTimeout(() => {
          context.dispatch('getStatus', payload)
          context.commit('incrementCounter')
        }, context.state.STATUS_INTERVAL_MS)
      }
    },
    getInstallmentsPaymentData(context, payload) {
      return payment.getInstallmentsPayment(payload)
    },
    postInstallmentsPaymentData(context, payload) {
      return payment.postInstallmentsPayment(payload)
    }
  }
}
